<template>
  <div class="genes-of-interest-panel" v-if="genes.length > 0">  
    <div class="panel-content">
      <div class="collapse-toggle" v-if="genes.length > 3">
        <el-button
          size="mini"
          type="text"
          @click="toggleCollapse"
          class="toggle-btn"
        >
          <i :class="isCollapsed ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ isCollapsed ? 'Show More' : 'Show Less' }} ({{ genes.length }})
        </el-button>
      </div>
      <div class="genes-display">
        <el-tag
          v-for="gene in visibleGenes"
          :key="gene"
          closable
          @close="removeGene(gene)"
          class="gene-tag"
          size="small"
        >
          {{ gene }}
        </el-tag>
        
        <el-button
          v-if="hasHiddenGenes"
          size="mini"
          type="primary"
          plain
          icon="el-icon-more"
          class="show-more-btn"
          @click="showAllDialog = true"
        >
          +{{ hiddenGenesCount }} more
        </el-button>
      </div>
    </div>
    <div class="panel-header">
      <span class="title">Selected Proteins: {{ genes.length }}</span>
      <div class="header-actions">
        <el-button 
          type="primary"
          size="mini"
          icon="el-icon-right"
          class="goi-btn use-btn"
          @click="useGenesForAnalysis"
        >
          Use for Analysis
        </el-button>
        <el-button 
          type="danger" 
          plain
          size="mini"
          icon="el-icon-delete"
          class="goi-btn"
          @click="clearAllGenes"
        >
          Clear
        </el-button>
      </div>
    </div>

    <!-- All Proteins Dialog -->
    <el-dialog
      title="All Selected Proteins"
      :visible.sync="showAllDialog"
      width="700px"
      :close-on-click-modal="true"
    >
      <div class="dialog-header-actions">
        <el-input
          v-model="searchQuery"
          placeholder="Search proteins..."
          prefix-icon="el-icon-search"
          size="small"
          clearable
          style="width: 300px; margin-bottom: 16px;"
        />
        <div style="flex: 1"></div>
        <el-button size="small" @click="exportGenes">
          <i class="el-icon-download"></i> Export
        </el-button>
      </div>
      
      <div class="all-genes-grid">
        <el-tag
          v-for="gene in filteredGenes"
          :key="gene"
          closable
          @close="removeGene(gene)"
          class="gene-tag-large"
          size="medium"
          type="info"
        >
          {{ gene }}
        </el-tag>
      </div>
      
      <div class="dialog-footer-info">
        <span>Total: {{ filteredGenes.length }} protein(s)</span>
        <span v-if="searchQuery">{{ filteredGenes.length }} of {{ genes.length }} shown</span>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'GenesOfInterest',
  props: {
    genes: {
      type: Array,
      default: () => []
    },
    maxVisible: {
      type: Number,
      default: 20
    }
  },
  data() {
    return {
      showAllDialog: false,
      searchQuery: '',
      isCollapsed: true
    }
  },
  computed: {
    visibleGenes() {
      if (this.isCollapsed && this.genes.length > 3) {
        return this.genes.slice(0, 3)
      }
      return this.genes.slice(0, this.maxVisible)
    },
    hasHiddenGenes() {
      const displayLimit = this.isCollapsed && this.genes.length > 3 ? 3 : this.maxVisible
      return this.genes.length > displayLimit
    },
    hiddenGenesCount() {
      const displayLimit = this.isCollapsed && this.genes.length > 3 ? 3 : this.maxVisible
      return Math.max(0, this.genes.length - displayLimit)
    },
    filteredGenes() {
      if (!this.searchQuery) {
        return this.genes
      }
      const query = this.searchQuery.toLowerCase()
      return this.genes.filter(gene => 
        gene.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
    },
    removeGene(gene) {
      this.$emit('remove-gene', gene)
    },
    clearAllGenes() {
      this.$confirm('This will clear all selected proteins. Continue?', 'Warning', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.$emit('clear-all')
      }).catch(() => {
        // Cancelled
      })
    },
    useGenesForAnalysis() {
      this.$emit('use-genes')
    },
    exportGenes() {
      const content = this.filteredGenes.join('\n')
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `selected_proteins_${new Date().getTime()}.txt`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$notify({
        title: 'Success',
        message: 'Proteins exported successfully',
        type: 'success',
        position: 'top-right'
      })
    }
  }
}
</script>

<style scoped>
.genes-of-interest-panel {
  margin-top: -10px;
  background: transparent;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  background: transparent;
}

.title {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 6px;
}

.goi-btn {
  padding: 4px 10px;
}

.goi-btn.use-btn {
  background: #3b82f6;
  border-color: #3b82f6;
  font-weight: 600;
}

.goi-btn.use-btn:hover {
  background: #2563eb;
  border-color: #2563eb;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.panel-content {
  padding: 6px 0;
}

.collapse-toggle {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.toggle-btn {
  padding: 4px 8px;
  font-size: 12px;
  color: #606266;
  transition: color 0.2s ease;
}

.toggle-btn:hover {
  color: #409EFF;
}

.toggle-btn i {
  margin-right: 4px;
  font-size: 12px;
}

.genes-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.gene-tag {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  background: #fff;
  border: 1px solid #ddd;
  color: #333;
  padding: 2px 8px;
}

.gene-tag:hover {
  border-color: #999;
}

.show-more-btn {
  padding: 2px 8px;
  font-size: 12px;
}

.dialog-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.all-genes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
}

.gene-tag-large {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  background: #fff;
  border: 1px solid #ddd;
  color: #333;
  padding: 4px 10px;
}

.gene-tag-large:hover {
  border-color: #999;
}

.dialog-footer-info {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #eee;
  font-size: 12px;
  color: #777;
}
</style>

